import { useTranslation } from "react-i18next";
import {
  Alert,
  Anchor,
  Grid,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { hasLength, useForm } from "@mantine/form";
import { useResetPassword } from "../api/queries";
import { MIN_PASSWORD_LENGTH } from "../api/types";
import { useLink } from "../router";
import { Chip } from "./Chip";
import { Header } from "./Header";
import { Shell } from "./Shell";
import { FeedbackLine } from "./FeedbackLine";

/**
 * The page an emailed reset link lands on.
 *
 * Unlike `ConfirmPage` there is nothing to fire on arrival: the token is spent by the submit,
 * together with the password it is being spent on. That also means no StrictMode guard is
 * needed here -- there is no effect, and a person cannot double-click their way into spending
 * the token twice because the second attempt is a 400 the form shows verbatim.
 */
export function ResetPage() {
  const { t } = useTranslation();
  const reset = useResetPassword();
  const token = new URLSearchParams(window.location.search).get("token");
  const home = useLink("/");
  const form = useForm({
    mode: "uncontrolled",
    initialValues: { password: "" },
    validate: {
      password: hasLength(
        { min: MIN_PASSWORD_LENGTH },
        t("registerDialog.passwordTooShort", { min: MIN_PASSWORD_LENGTH }),
      ),
    },
  });

  return (
    <Shell>
      <Shell.Header>
        <Header />
      </Shell.Header>
      <Shell.Main style={{ padding: 20 }}>
        <Grid columns={100}>
          <Grid.Col span={{ base: 100, sm: 80, md: 55 }}>
            <Paper p="md" m={{ base: 0, sm: 20 }} style={{ overflowY: "auto", minHeight: 0 }}>
              <Title order={2} fz="lg" lh={1.5} mb={6}>
                {t("resetPage.heading")}
              </Title>
              <Stack gap="sm">
                {!token ? (
                  <Alert color="red" variant="light">
                    {t("confirmPage.missingToken")}
                  </Alert>
                ) : reset.isSuccess ? (
                  <>
                    <Text>{t("resetPage.success")}</Text>
                    <Anchor {...home}>{t("confirmPage.goToArchive")}</Anchor>
                  </>
                ) : (
                  <form
                    onSubmit={form.onSubmit(({ password }) => {
                      void reset.mutateAsync({ token, password }).catch(() => {
                        // Shown from the mutation's own error below. Swallowed here so an
                        // expired link is a message rather than an unhandled rejection.
                      });
                    })}
                  >
                    <Stack gap="sm">
                      <PasswordInput
                        data-autofocus
                        label={t("resetPage.newPassword")}
                        name="password"
                        autoComplete="new-password"
                        description={t("resetPage.passwordHint", { count: MIN_PASSWORD_LENGTH })}
                        key={form.key("password")}
                        {...form.getInputProps("password")}
                      />
                      {/* The server's wording, not a paraphrase: "this link is invalid or has
                          expired" is its sentence and already written for a person to read. */}
                      <FeedbackLine role="alert">
                        {reset.isError && (reset.error as Error).message}
                      </FeedbackLine>
                      <Group justify="flex-end" gap="xs">
                        <Chip type="submit" variant="accent" disabled={reset.isPending}>
                          {reset.isPending ? t("common.saving") : t("resetPage.submit")}
                        </Chip>
                      </Group>
                    </Stack>
                  </form>
                )}
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Shell.Main>
    </Shell>
  );
}
